import classNames from 'classnames';
import React, { useCallback } from 'react';
import * as styles from './PostTopicListItem.module.scss';
import useIsPostTopicBarLarge from './useIsPostTopicBarLarge';
import PopOverArrow from '../../components/PopOverArrow';
import type { PopOverPrimitiveAllowControlledProps } from '../../components/PopOverPrimitive';
import PopOverPrimitive from '../../components/PopOverPrimitive';
import contentPreviewPopOverSize from '../../utils/popover/contentPreviewPopOverSize';
import useIsLayoutSize from '../utils/useIsLayoutSize';
import { LayoutSize } from '../utils/useLayoutSize';

interface PostTopicListItemRootProps {
  children: React.ReactNode;
  'aria-label'?: string;
  className?: string;
  style?: React.CSSProperties;
}

const PostTopicListItemRoot = React.forwardRef<
  HTMLLIElement,
  PostTopicListItemRootProps
>(({ children, className, style, 'aria-label': ariaLabel }, ref) => {
  const isLarge = useIsPostTopicBarLarge();

  return (
    <li
      ref={ref}
      aria-label={ariaLabel}
      style={style}
      className={classNames(className, styles.container, {
        [styles.large]: isLarge,
      })}
    >
      {children}
    </li>
  );
});

interface PostTopicItemContentProps {
  name: React.ReactNode;
  description?: React.ReactNode;
  icon: React.ReactNode;
}

const PostTopicListItemContent = ({
  icon,
  name,
  description,
}: PostTopicItemContentProps) => (
  <div className={styles.contentContainer}>
    <div className={styles.icon}>{icon}</div>
    <div className={styles.content}>
      <PopOverPrimitive.Anchor>
        <div className={styles.popOverAnchor} />
      </PopOverPrimitive.Anchor>
      {!!description && <div className={styles.description}>{description}</div>}
      <div className={styles.name}>{name}</div>
    </div>
  </div>
);

const SCREEN_PADDING = 12;

type PostTopicListItemPopOverProps = {
  popOver: React.ReactNode;
  children: React.ReactNode;
  hidden?: boolean;
  naturalWidth?: number;
  trigger?: 'hover' | 'clickOrHover';
} & PopOverPrimitiveAllowControlledProps;

const PostTopicListItemPopOver = ({
  children,
  popOver,
  naturalWidth,
  hidden,
  trigger,
  ...props
}: PostTopicListItemPopOverProps) => {
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const getAdditionalMiddleware = useCallback<
    NonNullable<
      React.ComponentProps<typeof PopOverPrimitive>['getAdditionalMiddleware']
    >
  >(
    (options) => [
      contentPreviewPopOverSize({
        ...options,
        fullWidth: isMobile,
        naturalWidth,
      }),
    ],
    [isMobile, naturalWidth],
  );

  return (
    <PopOverPrimitive
      hoverGroupId="post-topic-list-item"
      screenPadding={SCREEN_PADDING}
      trigger={trigger}
      placement="bottom-start"
      enabledForTouch
      hideWhenReferenceHidden
      hoverDelay="slow"
      getAdditionalMiddleware={getAdditionalMiddleware}
      noFlip
      hidden={hidden}
      {...props}
    >
      <PopOverPrimitive.Trigger asChild>
        <div className={styles.popOverTrigger}>{children}</div>
      </PopOverPrimitive.Trigger>

      <PopOverPrimitive.Floating className={styles.popOverFloatingContainer}>
        <PopOverPrimitive.Content className={styles.popOverInner}>
          {popOver}
        </PopOverPrimitive.Content>
        {!hidden && <PopOverArrow className={styles.popOverArrow} />}
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

export default {
  Root: PostTopicListItemRoot,
  Content: PostTopicListItemContent,
  PopOver: PostTopicListItemPopOver,
};
